import { DeleteTwoTone, PlusCircleTwoTone } from '@ant-design/icons'
import { Button, Popconfirm } from 'antd'
import './index.css'
import { TabularFormItemsProps } from './types'
import HelpIcon from '../HelpIcon'
import CIMSLink from '../CIMSLink'
import ProjectLink from '../ProjectLink'
import { useKeyedBooleans } from 'hooks/useKeyedBooleans'
import { Fragment } from 'react'

/**
 * this is breaking the rules. for most components, you should use Ant Design
 * components to design your component
 *
 * Ant Design's table is too strictly formatted and we need specific styling
 * for kind of table structure
 *
 * @see {@link TabularFormItemsProps}
 */
function TabularFormItems<T extends object>({
  columns,
  records = [],
  rowKey,
  add,
  remove,
  recordName,
  extra,
}: TabularFormItemsProps<T>) {
  const [removing, setRemoving] = useKeyedBooleans()
  const resolved = Array.isArray(records) ? records : [records]
  return (
    <table className="cims-tabular-form-items-table">
      {(add || extra) && (
        <thead>
          <tr>
            <th colSpan={columns.length + (remove ? 1 : 0)}>
              <div className="cims-tabular-form-items-table-add-extra-row">
                {add && (
                  <Button
                    key="add-button"
                    type="primary"
                    icon={<PlusCircleTwoTone />}
                    {...add}
                  >
                    {recordName}
                  </Button>
                )}
                {extra && <Fragment key="extra">{extra}</Fragment>}
              </div>
            </th>
          </tr>
        </thead>
      )}
      <thead>
        <tr>
          {columns.map(
            ({
              title: initialTitle,
              key,
              help,
              link,
              maxWidth,
              minWidth,
              width,
            }) => {
              let title = <HelpIcon text={help}>{initialTitle}</HelpIcon>
              if (typeof link === 'string')
                title = (
                  <CIMSLink external href={link}>
                    {title}
                  </CIMSLink>
                )
              else if (link)
                title = <ProjectLink item={link}>{title}</ProjectLink>
              return (
                <th key={key} style={{ maxWidth, minWidth, width }}>
                  <div className="cims-tabular-form-items-table-cell">
                    {title}
                  </div>
                </th>
              )
            }
          )}
          {remove && <th />}
        </tr>
      </thead>
      <tbody>
        {resolved.map((record) => {
          const { onClick, disabled, ...removeButtonProps } = remove ?? {}
          const key =
            typeof rowKey === 'function'
              ? rowKey(record)
              : String(record[rowKey])
          return (
            <tr key={key} id={`${key}_row`}>
              {columns.map(
                ({ key: childKey, render, title: _title, ...style }) => (
                  <td id={`${key}-${childKey}`} key={childKey} style={style}>
                    <div className="cims-tabular-form-items-table-cell">
                      {render(record)}
                    </div>
                  </td>
                )
              )}
              {remove && (
                <td
                  key={`remove-${key}`}
                  className="cims-tabular-form-items-table-remove-button"
                >
                  <Popconfirm
                    key={`popconfirm-${key}`}
                    title={`Are you sure you want to remove this ${
                      recordName ?? 'row'
                    }? This action cannot be undone!`}
                    onConfirm={async () => {
                      setRemoving.add(key)
                      await onClick?.(record)
                      setRemoving.remove(key)
                    }}
                    /**
                     * for some reason the overlay
                     */
                    overlayInnerStyle={{ maxWidth: 300 }}
                  >
                    <Button
                      disabled={removing(key) || disabled}
                      loading={removing(key)}
                      type="text"
                      icon={<DeleteTwoTone />}
                      {...removeButtonProps}
                    />
                  </Popconfirm>
                </td>
              )}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export default TabularFormItems
